import { AppAction } from '../actions';
import { ApiCaller, AuthApiCaller } from '../../config';
import { put, call } from 'redux-saga/effects';
import { Utils } from '../../config';
import { registerDeviceForPush, unregisterDeviceForPush } from '../../config/firebase/firebase';
import { readJSON, remove, setAppLocked, clearAppLocked } from '../../config/storage';
import { STORAGE_KEYS, PAGE_SIZE } from '../../config/constants';

/**
 * Ceiling on a best-effort remote step during sign-out. Long enough for a slow
 * mobile connection, short enough that a hung service worker or a stalled
 * request cannot hold the user on a screen they have signed out of.
 */
const LOGOUT_STEP_TIMEOUT_MS = 4000;

/**
 * Resolves with the promise, or rejects once the ceiling passes.
 *
 * `Promise.race` rather than a plain `await`: the callers here are optional
 * side effects, and the failure that mattered was not an error but a promise
 * that never settled at all.
 */
const withTimeout = (promise, ms) =>
  Promise.race([
    Promise.resolve(promise),
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
  ]);

export default class AppMiddleware {
  // Shared session persistence for both password login and the 2FA verify step.
  // Auth's response carries only { access, refresh }; the local user profile
  // (level / status / id / avatar …) is fetched from the backend and merged into
  // the flat legacy shape the reducer, route guards, and callbacks expect.
  static *persistAuthSession(res, cb) {
    const { access, refresh } = res?.data?.data ?? {};
    localStorage.setItem('auth_token', JSON.stringify(access));
    if (refresh) localStorage.setItem('refresh_token', JSON.stringify(refresh));

    let user = { data: { auth_token: access, refresh_token: refresh } };
    try {
      const profileRes = yield ApiCaller.Get('/api/v1/user/profile');
      user = {
        ...profileRes?.data,
        data: { ...profileRes?.data?.data, auth_token: access, refresh_token: refresh },
      };
    } catch {
      /* tokens are stored; profile will be re-fetched by UserProfile */
    }

    localStorage.setItem('user', JSON.stringify(user));

    // Register this device now that a session exists. Login itself goes straight
    // to auth, which cannot deliver push (no Firebase credentials there), so the
    // backend only learns about the device through this call.
    try {
      yield registerDeviceForPush();
    } catch {
      /* best-effort — never block sign-in on push registration */
    }

    yield put(AppAction.SignInSuccess(user));
    cb && cb(user);
  }

  static *SignIn(params) {
    const { payload, cb } = params;
    try {
      // Credentials go DIRECTLY to central auth (SynapseAuth), which returns
      // either { access, refresh, sub } or { mfa_required, mfa_token } when the
      // account has 2FA enabled. `_public` so a 401 (bad credentials) surfaces
      // instead of triggering a token refresh / hard-logout.
      let res = yield AuthApiCaller.Post(
        '/v1/auth/login',
        {
          identifier: payload?.login_id || payload?.email,
          password: payload?.password,
          app: 'synapse',
          platform: payload?.platform === 'ADMIN' ? 'admin' : 'user',
          device: { platform: payload?.platform, fcm_token: payload?.fcm_token ?? null },
        },
        { _public: true }
      );
      if (res.status == 200) {
        const data = res?.data?.data ?? {};

        // 2FA gate: password verified but no session yet. Surface the challenge
        // ticket to the UI (SignIn switches to the code step); do NOT store tokens.
        if (data?.mfa_required) {
          yield put(AppAction.SignInFailure()); // release the button loader
          cb && cb({ mfaRequired: true, mfaToken: data?.mfa_token });
          return;
        }

        // No success toast: the redirect into the app is the confirmation, and the
        // notification outlived the screen it was raised on. Failure still toasts —
        // there the reason is the only thing the user gets.
        yield* AppMiddleware.persistAuthSession(res, cb);
      } else {
        yield put(AppAction.SignInFailure());
      }
    } catch (err) {
      yield put(AppAction.SignInFailure());
      Utils.showMessage('error', err?.response?.data?.message || 'Incorrect login ID or password');
    }
  }

  // Completes a 2FA-gated login: exchanges the mfa_token + code for a session.
  static *Verify2fa(params) {
    const { payload, cb } = params;
    try {
      let res = yield AuthApiCaller.Post(
        '/v1/auth/2fa/verify',
        { mfa_token: payload?.mfaToken, code: payload?.code },
        { _public: true }
      );
      if (res.status == 200) {
        yield* AppMiddleware.persistAuthSession(res, cb);
      } else {
        yield put(AppAction.Verify2faFailure());
      }
    } catch (err) {
      yield put(AppAction.Verify2faFailure());
      Utils.showMessage('error', err?.response?.data?.message || 'Invalid or expired code');
    }
  }

  static *UserProfile(params) {
    const { cb } = params;
    try {
      let res = yield ApiCaller.Get('/api/v1/user/profile');
      if (res.status == 200) {
        yield put(AppAction.UserProfileSuccess(res?.data));
        cb && cb(res?.data);
        // Preserve the live access token: the profile row's `auth_token` column
        // is null under central auth, so re-persist the token we already hold
        // (and never overwrite it from the profile response).
        const currentToken = JSON.parse(localStorage.getItem('auth_token') || 'null');
        const merged = {
          ...res?.data,
          data: { ...res?.data?.data, auth_token: currentToken },
        };
        localStorage.setItem('user', JSON.stringify(merged));
      } else {
        yield put(AppAction.UserProfileFailure());
      }
    } catch {
      yield put(AppAction.UserProfileFailure());
    }
  }

  static *ChangePassword(params) {
    const { payload, cb } = params;

    const mainData = {
      old_password: payload?.old_Password,
      new_password: payload?.Password,
    };
    try {
      // Direct to central auth (requireAuth — sends the Bearer access token).
      let res = yield AuthApiCaller.Post('/v1/auth/password/change', mainData);

      if (res.status == 200) {
        yield put(AppAction.ChangePasswordSuccess(res?.data));
        Utils.showMessage('success', res?.data?.message ? res?.data?.message : 'Password changed');
        cb && cb();
      } else {
        yield put(AppAction.ChangePasswordFailure());
      }
    } catch {
      yield put(AppAction.ChangePasswordFailure());
    }
  }

  /**
   * Sign out.
   *
   * Signing out is a LOCAL operation that also tells the server. It used to be
   * written the other way round — two remote calls, then the local teardown and
   * the caller's redirect — which gave it two ways to strand the user on a
   * signed-in screen that no longer had a session:
   *
   * - Neither remote call was bounded. `unregisterDeviceForPush` talks to the
   *   service worker and FCM, and on a mobile browser (no push permission, a
   *   sleeping or unregistered worker) that can sit unresolved indefinitely. A
   *   `try/catch` does not help: a promise that never settles is not an error,
   *   so the saga simply parked and `cb` — which is what performs the redirect —
   *   was never reached.
   * - `JSON.parse(localStorage.getItem('refresh_token'))` sat between the two
   *   `try` blocks, unguarded. One malformed entry threw, and a throw inside a
   *   saga ends it: no local clear, no dispatch, no redirect.
   *
   * Both remote calls are best-effort and time-boxed now, and the local teardown
   * runs in `finally`, so the session is always cleared and the caller always
   * gets its callback.
   */
  static *Logout(params) {
    const { cb } = params;

    try {
      // Unregister THIS device first — the endpoint is authenticated, so it has
      // to run while the access token is still valid. Only this device stops
      // receiving push; the user's other sessions are untouched.
      yield withTimeout(unregisterDeviceForPush(), LOGOUT_STEP_TIMEOUT_MS);
    } catch {
      /* best effort — logout proceeds regardless */
    }

    try {
      // Revoke the session at central auth. `_public` because logout tolerates
      // an already-expired token.
      const refresh = readJSON(STORAGE_KEYS.REFRESH_TOKEN);
      yield withTimeout(
        AuthApiCaller.Post('/v1/auth/logout', { refresh }, { _public: true }),
        LOGOUT_STEP_TIMEOUT_MS
      );
    } catch {
      /* best effort — the local session is cleared either way */
    } finally {
      // Storage FIRST. From here a reload cannot restore the session, which is
      // what makes it safe to hand the transition to the caller below.
      remove(STORAGE_KEYS.AUTH_TOKEN);
      remove(STORAGE_KEYS.REFRESH_TOKEN);
      remove(STORAGE_KEYS.USER);
      // Was a bare `localStorage.removeItem` inside the LOGOUT_SUCCESS reducer case.
      // It belongs with the rest of the session teardown, which is here.
      clearAppLocked();

      /**
       * The callback owns the transition, and if there is one the store is not
       * worth resetting.
       *
       * `LOGOUT_SUCCESS` clears `user`, `AuthRoutes` reacts to that by navigating
       * to the sign-in screen, and THEN the callback replaced the document — so
       * the user watched the login screen render and immediately reload. That is
       * the "logout, then a refresh" behaviour. A replaced document arrives with
       * an empty store, a closed socket and no timers, so dispatching into the
       * one being thrown away bought nothing and cost the flash.
       *
       * With no callback nobody is navigating and the store is all there is, so
       * it has to be reset here.
       */
      if (cb) {
        cb();
      } else {
        yield put(AppAction.LogoutSuccess());
      }
    }
  }

  static *LastSeenToggle(action) {
    const { payload, cb } = action;
    try {
      let res = yield ApiCaller.Patch('/api/v1/user/last-seen-toggle', {
        enabled: payload.enabled,
      });

      if (res.status === 200) {
        yield put(AppAction.LastSeenToggleSuccess(res?.data));

        // refresh profile so frontend stays in sync
        yield put(AppAction.UserProfile());

        cb && cb(res?.data);
      } else {
        yield put(AppAction.LastSeenToggleFailure());
      }
    } catch {
      yield put(AppAction.LastSeenToggleFailure());
    }
  }

  /**
   * Persist the app-lock latch.
   *
   * Both of these were `localStorage.setItem` calls inside the AppReducer cases for
   * APP_LOCK_TRIGGER / APP_UNLOCK — a blocking synchronous write on the dispatch path,
   * from a function that is required to be pure. The reducer still owns the in-memory
   * latch; only the durable copy moved here.
   */
  static *PersistAppLock() {
    // `call` rather than invoking directly: it keeps the write an inspectable effect,
    // which is the only reason these are sagas rather than inline in the reducer.
    yield call(setAppLocked, true);
  }

  static *PersistAppUnlock() {
    yield call(setAppLocked, false);
  }

  //CONTACT_MIDDLEWARES
  static *CreateContact({ payload, cb }) {
    try {
      const res = yield ApiCaller.Post('/api/v1/contacts', payload, {
        notification: false,
      });
      if (res.status === 200 || res.status === 201) {
        // Silent on success, like the delete and update below: the contact list
        // re-renders with the change, which says it better than a notification.
        // Failures still toast — there is nothing on screen to read instead.
        yield put(AppAction.CreateContactSuccess(res.data));
        cb && cb(res.data);
      }
    } catch {
      yield put(AppAction.CreateContactFailure());
    }
  }

  static *DeleteContact({ payload, cb }) {
    try {
      const res = yield ApiCaller.Delete(`/api/v1/contacts/${payload}`);
      if (res.status === 200) {
        yield put(AppAction.DeleteContactSuccess(payload));
        cb && cb();
      }
    } catch {
      yield put(AppAction.DeleteContactFailure());
      Utils.showMessage('error', 'Failed to delete contact');
      cb && cb();
    }
  }

  static *UpdateContact({ payload, cb }) {
    try {
      const { id, ...data } = payload;
      const res = yield ApiCaller.Patch(`/api/v1/contacts/${id}`, data);

      if (res.status === 200) {
        yield put(AppAction.UpdateContactSuccess(payload));
        cb && cb(payload);
      }
    } catch {
      yield put(AppAction.UpdateContactFailure());
      Utils.showMessage('error', 'Failed to update contact');
    }
  }

  static *ListContacts({ payload, cb }) {
    try {
      const { limit = PAGE_SIZE, offset = 0, search = '' } = payload || {};
      // `URLSearchParams` rather than interpolation: the search term is user input and
      // was previously spliced into the query string unescaped.
      const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (search) query.set('search', search);
      const res = yield ApiCaller.Get(`/api/v1/contacts?${query.toString()}`);

      const rows = res?.data?.data.rows || [];
      const count = res?.data?.data.count || 0;
      // Offset 0 is a fresh first page, not more of the current one.
      yield put(AppAction.ListContactsSuccess({ rows, count, replace: Number(offset) === 0 }));
      cb && cb({ rows, count });
    } catch {
      yield put(AppAction.ListContactsFailure());
    }
  }

  // Toggle Passcode (enable/disable)
  static *PasscodeToggle({ payload, cb }) {
    // payload contains { status, passcode, lock_time }
    try {
      const res = yield ApiCaller.Post('/api/v1/user/passcode-toggle', payload);

      if (res.status === 200) {
        const storedUser = readJSON(STORAGE_KEYS.USER, {}) ?? {};
        if (storedUser && storedUser.data) {
          storedUser.data.passcode_settings = {
            is_passcode_enabled: payload.status,
            lock_time: payload.lock_time,
          };
          localStorage.setItem('user', JSON.stringify(storedUser));
        }

        yield put(
          AppAction.PasscodeToggleSuccess({
            is_passcode_enabled: payload.status,
            lock_time: payload.lock_time,
          })
        );
        cb && cb(res.data);
        const successMsg = payload.passcode
          ? 'Passcode set successfully'
          : payload.status
            ? 'Passcode enabled'
            : 'Passcode disabled';
        Utils.showMessage('success', res?.data?.message ?? successMsg);
      } else {
        yield put(AppAction.PasscodeToggleFailure());
        Utils.showMessage('error', res?.data?.message ?? 'Failed to update passcode');
      }
    } catch {
      yield put(AppAction.PasscodeToggleFailure());
      Utils.showMessage('error', 'An error occurred while updating passcode');
    }
  }

  // Verify passcode
  static *VerifyPasscode({ payload, cb }) {
    try {
      const res = yield ApiCaller.Post('/api/v1/user/passcode/verify', payload);
      if (res.status === 200) {
        yield put(AppAction.VerifyPasscodeSuccess());
        cb && cb();
      } else {
        yield put(AppAction.VerifyPasscodeFailure());
      }
    } catch {
      yield put(AppAction.VerifyPasscodeFailure());
    }
  }

  // Update passcode settings
  static *UpdatePasscode({ payload, cb }) {
    try {
      const res = yield ApiCaller.Patch('/api/v1/user/passcode', payload);
      if (res.status === 200) {
        const storedUser = readJSON(STORAGE_KEYS.USER, {}) ?? {};
        if (storedUser && storedUser.data) {
          storedUser.data.passcode_settings = {
            ...storedUser.data.passcode_settings,
            lock_time: payload.lockTime,
          };
          localStorage.setItem('user', JSON.stringify(storedUser));
        }

        // Reducer expects { lockTime, passcode } which matches payload
        yield put(AppAction.UpdatePasscodeSuccess(payload));
        cb && cb(res.data);
        Utils.showMessage('success', 'Passcode updated successfully');
      } else {
        yield put(AppAction.UpdatePasscodeFailure());
        Utils.showMessage('error', res?.data?.message ?? 'Failed to update passcode');
      }
    } catch {
      yield put(AppAction.UpdatePasscodeFailure());
      Utils.showMessage('error', 'An error occurred while updating passcode');
    }
  }

  static *ContactSupport(params) {
    const { payload, cb } = params;
    try {
      const res = yield ApiCaller.Post('/api/v1/auth/contact-support', payload);
      yield put(AppAction.ContactSupportSuccess(res?.data));
      cb && cb(res?.data);
    } catch (error) {
      yield put(AppAction.ContactSupportFailure());
      /**
       * axios rejects on every non-2xx, so a refusal can ONLY arrive here — the
       * `else` branch that used to sit under a `res.status == 200` check was
       * unreachable.
       *
       * Pass the API envelope straight through. Synthesising
       * `{ status: 500, message: 'An error occurred' }` threw away a perfectly
       * good 400 ("User account not found.") and relabelled it a server fault,
       * which the caller then raised as a second, wronger toast on top of the
       * accurate one the interceptor had already shown.
       */
      cb &&
        cb(error?.response?.data ?? { success: false, message: 'Network error. Please retry.' });
    }
  }
}
